// oxlint-disable-next-line no-restricted-imports -- This is the Sonner boundary for app toasts.
import { toast as sonnerToast } from 'sonner';

import { getApiClient } from '@apps/web/lib/api_client';
import { type ToastNotificationTypeKey } from '@apps/web/lib/notification_channel_preferences';
import { type NotificationNavigationTarget } from '@apps/web/lib/notification_navigation';
import {
    type NotificationEntryInput,
    notificationStore,
} from '@apps/web/lib/notification_store';
import { toastPauseStore } from '@apps/web/lib/toast_pause_store';
import { type Mode } from '@libraries/typescript/api_client';

type ToastSeverity = 'info' | 'success' | 'warning' | 'error';
type ToastMessage = Parameters<typeof sonnerToast>[0];
type SonnerToastOptions = NonNullable<Parameters<typeof sonnerToast>[1]>;
type ToastResult = ReturnType<typeof sonnerToast>;
type ToastRenderer = ToastSeverity | 'default';

type NotificationResource = {
    readonly type: string;
    readonly id: string;
};

type AppNotificationContext = {
    readonly mode: Mode | undefined;
    readonly notificationPreferences: unknown;
    readonly organizationId: string | null;
};

type NotificationBackedToastOptions = SonnerToastOptions & {
    readonly persistNotification: true;
    readonly notificationId?: string;
    readonly notificationMode?: Mode | null;
    readonly notificationNavigationTarget?: NotificationNavigationTarget | null;
    readonly notificationRelationships?: ReadonlyArray<NotificationResource>;
    readonly notificationResource?: NotificationResource;
    readonly notificationType: ToastNotificationTypeKey;
    /**
     * Renders even when pop-ups are globally off or paused. Reserved for
     * feedback about the mute action itself — without it, "Turn off all
     * pop-ups" would silence its own Undo affordance.
     */
    readonly alwaysShow?: boolean;
};

type DisplayOnlyToastOptions = SonnerToastOptions & {
    readonly notificationId?: never;
    readonly notificationMode?: never;
    readonly notificationNavigationTarget?: never;
    readonly notificationRelationships?: never;
    readonly notificationResource?: never;
    readonly notificationType?: never;
    readonly persistNotification: false;
    /**
     * Renders even when pop-ups are globally off or paused. Reserved for
     * feedback about the mute action itself — without it, "Turn off all
     * pop-ups" would silence its own Undo affordance.
     */
    readonly alwaysShow?: boolean;
};

type PhoebeToastOptions =
    | DisplayOnlyToastOptions
    | NotificationBackedToastOptions;

type SonnerOptionsForDisplay = Omit<
    PhoebeToastOptions,
    | 'persistNotification'
    | 'notificationId'
    | 'notificationMode'
    | 'notificationNavigationTarget'
    | 'notificationRelationships'
    | 'notificationResource'
    | 'notificationType'
    | 'alwaysShow'
>;

const DEFAULT_CONTEXT: AppNotificationContext = {
    mode: undefined,
    notificationPreferences: undefined,
    organizationId: null,
};

let appNotificationContext = DEFAULT_CONTEXT;

function generateNotificationId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }

    return '10000000-1000-4000-8000-000000000000'.replace(/[018]/g, (c) =>
        (Number(c) ^ ((Math.random() * 16) >> (Number(c) / 4))).toString(16),
    );
}

function getToastTitle(severity: ToastSeverity, message: ToastMessage): string {
    if (typeof message === 'string' && message.trim().length > 0) {
        return message;
    }

    switch (severity) {
        case 'success':
            return 'Success';
        case 'error':
            return 'Error';
        case 'warning':
            return 'Warning';
        case 'info':
            return 'Info';
        default:
            return 'Notification';
    }
}

function getToastDescription(options: PhoebeToastOptions | undefined): string {
    const description = options?.description;
    return typeof description === 'string' ? description : '';
}

function splitSonnerOptions(
    options: PhoebeToastOptions,
): SonnerOptionsForDisplay {
    const {
        persistNotification: _persistNotification,
        notificationId: _notificationId,
        notificationMode: _notificationMode,
        notificationNavigationTarget: _notificationNavigationTarget,
        notificationRelationships: _notificationRelationships,
        notificationResource: _notificationResource,
        notificationType: _notificationType,
        alwaysShow: _alwaysShow,
        ...sonnerOptions
    } = options;
    return sonnerOptions;
}

function callSonnerToast(
    displayToast: (
        message: ToastMessage,
        options?: SonnerOptionsForDisplay,
    ) => ToastResult,
    message: ToastMessage,
    options: SonnerOptionsForDisplay | undefined,
): ToastResult {
    if (options === undefined) {
        return displayToast(message);
    }
    return displayToast(message, options);
}

function shouldPersistNotification(
    _notificationType: ToastNotificationTypeKey,
): boolean {
    return false;
}

function buildNotificationEntry(options: {
    readonly id: string;
    readonly message: string;
    readonly notificationType: ToastNotificationTypeKey;
    readonly resource: NotificationResource;
    readonly severity: ToastSeverity;
    readonly title: string;
    readonly toastOptions: NotificationBackedToastOptions;
}): NotificationEntryInput {
    return {
        id: options.id,
        type: `notification_${options.severity}`,
        notificationType: options.notificationType,
        title: options.title,
        message: options.message,
        resourceType: options.resource.type,
        resourceId: options.resource.id,
        timestamp: new Date().toISOString(),
        mode:
            options.toastOptions.notificationMode ??
            appNotificationContext.mode ??
            null,
        navigationTarget:
            options.toastOptions.notificationNavigationTarget ?? null,
        isRead: false,
        isDismissed: false,
    };
}

function persistToastNotification(options: {
    readonly entry: NotificationEntryInput;
    readonly notificationType: ToastNotificationTypeKey;
    readonly severity: ToastSeverity;
    readonly toastOptions: NotificationBackedToastOptions;
}) {
    const organizationId = appNotificationContext.organizationId;
    if (organizationId === null) {
        return;
    }

    void getApiClient()
        .post({
            url: '/organizations/{organization_id}/notifications/toast',
            pathParams: {
                organization_id: organizationId,
            },
            body: {
                notification_id: options.entry.id,
                idempotency_key: options.entry.id,
                notification_type: options.notificationType,
                severity: options.severity,
                title: options.entry.title,
                message: options.entry.message || null,
                resource: {
                    type: options.entry.resourceType,
                    id: options.entry.resourceId,
                },
                relationships:
                    options.toastOptions.notificationRelationships === undefined
                        ? []
                        : [...options.toastOptions.notificationRelationships],
                data: {
                    mode: options.entry.mode,
                },
            },
            reason: 'app-toast-notification',
        })
        .then((notification) => {
            notificationStore.upsertNotification({
                id: notification.id,
                type: notification.type,
                notificationType:
                    typeof notification.data?.['notification_type'] === 'string'
                        ? notification.data['notification_type']
                        : options.notificationType,
                title: notification.title ?? '',
                message: notification.message ?? '',
                resourceType: notification.resource_type,
                resourceId: notification.resource_id,
                timestamp: notification.timestamp,
                mode:
                    typeof notification.data?.['mode'] === 'string'
                        ? notification.data['mode']
                        : null,
                navigationTarget: options.entry.navigationTarget,
                isRead: notification.is_read,
                isDismissed: notification.is_dismissed,
            });
        })
        .catch(() => {
            // The toast itself is user feedback for another action. A failed
            // history write should not hide that feedback or fail the action.
        });
}

function isToastSuppressed(
    severity: ToastSeverity,
    options: PhoebeToastOptions,
): boolean {
    // Errors are frequently the only signal that an action failed. Silencing
    // them would leave the user with no indication anything went wrong, so
    // they ignore both the master switch and the pause.
    if (severity === 'error') {
        return false;
    }
    if (options.alwaysShow === true) {
        return false;
    }
    if (
        options.persistNotification &&
        options.notificationType === 'form_failure'
    ) {
        return false;
    }

    if (toastPauseStore.isPaused()) {
        return true;
    }

    const preferences = appNotificationContext.notificationPreferences;
    return (
        typeof preferences === 'object' &&
        preferences !== null &&
        'toast_global_enabled' in preferences &&
        (preferences as { readonly toast_global_enabled?: boolean })
            .toast_global_enabled === false
    );
}

function presentToast(
    severity: ToastSeverity,
    message: ToastMessage,
    options: PhoebeToastOptions,
    renderer: ToastRenderer = severity,
): ToastResult {
    if (isToastSuppressed(severity, options)) {
        return '';
    }

    if (
        options.persistNotification &&
        shouldPersistNotification(options.notificationType)
    ) {
        const title = getToastTitle(severity, message);
        const description = getToastDescription(options);
        const resource =
            options.notificationResource ??
            (appNotificationContext.organizationId === null
                ? null
                : {
                      type: 'organization',
                      id: appNotificationContext.organizationId,
                  });

        if (resource !== null) {
            const notificationId =
                options.notificationId ?? generateNotificationId();
            const entry = buildNotificationEntry({
                id: notificationId,
                message: description,
                notificationType: options.notificationType,
                resource,
                severity,
                title,
                toastOptions: options,
            });

            notificationStore.upsertNotification(entry);
            persistToastNotification({
                entry,
                notificationType: options.notificationType,
                severity,
                toastOptions: options,
            });
        }
    }

    const sonnerOptions = splitSonnerOptions(options);
    switch (renderer) {
        case 'success':
            return callSonnerToast(sonnerToast.success, message, sonnerOptions);
        case 'error':
            return callSonnerToast(sonnerToast.error, message, sonnerOptions);
        case 'warning':
            return callSonnerToast(sonnerToast.warning, message, sonnerOptions);
        case 'info':
            return callSonnerToast(sonnerToast.info, message, sonnerOptions);
        default:
            return callSonnerToast(sonnerToast, message, sonnerOptions);
    }
}

function presentMessage(
    message: ToastMessage,
    options: PhoebeToastOptions,
): ToastResult {
    return presentToast('info', message, options, 'default');
}

export function updateAppNotificationContext(
    context: AppNotificationContext,
): () => void {
    appNotificationContext = context;
    return () => {
        if (appNotificationContext === context) {
            appNotificationContext = DEFAULT_CONTEXT;
        }
    };
}

export const toast = Object.assign(presentMessage, {
    dismiss: sonnerToast.dismiss,
    error: (message: ToastMessage, options: PhoebeToastOptions) =>
        presentToast('error', message, options),
    info: (message: ToastMessage, options: PhoebeToastOptions) =>
        presentToast('info', message, options),
    message: presentMessage,
    success: (message: ToastMessage, options: PhoebeToastOptions) =>
        presentToast('success', message, options),
    warning: (message: ToastMessage, options: PhoebeToastOptions) =>
        presentToast('warning', message, options),
});
